import { z } from "zod";
import type { InteractionBlueprint } from "@exagens/behavioral-insight-console-client";
import { metadataSchema, type Metadata } from "@/lib/sparxs/metadataSchemas";
import {
  advisorContentSchema,
  contentItemSchema,
  type AdvisorContent,
  type ContentItem,
} from "./contentItemSchemas";
import { interactionBlueprintFieldsSchema } from "./contentItemFieldSchemas";

export type InteractionBlueprintData = Omit<
  InteractionBlueprint,
  "content" | "metadata" | "advisorContent"
> & {
  fileName: string;
  content: ContentItem[];
  metadata?: Metadata;
  metadataRaw?: unknown;
  interactionBlueprintRaw?: unknown;
  opaqueFieldPaths?: string[];
  advisorContent?: AdvisorContent;
};

const interactionBlueprintDataSchema = z.object({
  fileName: z.string().min(1),
  metadata: metadataSchema,
  content: z.array(contentItemSchema),
  advisorContent: advisorContentSchema.optional(),
  metadataRaw: z.unknown().optional(),
  interactionBlueprintRaw: z.unknown().optional(),
  opaqueFieldPaths: z.array(z.string()).optional(),
});

export { interactionBlueprintDataSchema };

export const parseInteractionBlueprintFromFields = (
  parsed: z.output<typeof interactionBlueprintFieldsSchema>,
  content: z.output<typeof contentItemSchema>[],
  advisorContent?: z.output<typeof advisorContentSchema>,
): InteractionBlueprintData => {
  const rawMeta: unknown = parsed.metadata;
  const metadataRaw =
    rawMeta != null && typeof rawMeta === "object" && !Array.isArray(rawMeta)
      ? (JSON.parse(JSON.stringify(rawMeta)) as unknown)
      : undefined;

  const merged =
    rawMeta != null && typeof rawMeta === "object" && !Array.isArray(rawMeta)
      ? ({ ...(rawMeta as Record<string, unknown>) } as Record<string, unknown>)
      : {};
  const metadata = metadataSchema.parse(merged);

  const resolvedFileName =
    typeof parsed.fileName === "string" && parsed.fileName.trim().length > 0
      ? parsed.fileName.trim()
      : null;
  if (resolvedFileName == null) {
    throw new Error(
      "parseInteractionBlueprintFromFields: interaction blueprint requires non-empty fileName",
    );
  }

  const parsedCore = interactionBlueprintDataSchema.parse({
    ...parsed,
    fileName: resolvedFileName,
    metadata,
    content,
    advisorContent,
  });

  return metadataRaw !== undefined
    ? { ...parsedCore, metadataRaw }
    : parsedCore;
};
